import { TasksBulkActionsStore } from "@components/modules/resource-planning/tasks/listing/store/bulk-actions";
import { TasksFiltersStore } from "@components/modules/resource-planning/tasks/store/filters";
import { TasksViewModeStore } from "@components/modules/resource-planning/tasks/store/view-mode";
import { t } from "@lingui/macro";
import {
  AllTaskIdsDocument,
  type AllTaskIdsQuery,
  type AllTaskIdsQueryVariables,
  type TaskBaseFragment,
  TaskWhereColumn,
} from "@src/__generated__/graphql";
import { TASK_ID_QUERY_KEY } from "@src/constants/tasks";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import type { AppStore } from "@src/stores/AppStore";
import { action, computed, makeObservable } from "mobx";
import type { TasksViewStore } from "./types";

export abstract class TasksBaseStore implements TasksViewStore {
  viewModeStore: TasksViewModeStore;
  filtersStore: TasksFiltersStore;
  bulkActionsStore: TasksBulkActionsStore;

  private taskIdsFetcher = new FetchHelper<
    AllTaskIdsQuery,
    AllTaskIdsQueryVariables
  >(AllTaskIdsDocument);

  /**
   * `paginationKey` names the persisted page size. A view that paginates over
   * something other than task rows passes its own, so it cannot overwrite the
   * rows-per-page of the listing.
   */
  constructor(
    protected readonly appStore: AppStore,
    paginationKey?: string,
  ) {
    makeObservable(this);
    this.viewModeStore = new TasksViewModeStore(appStore);
    this.filtersStore = new TasksFiltersStore(
      this,
      paginationKey ?? this.viewModeStore.getPaginationKey,
    );
    this.bulkActionsStore = new TasksBulkActionsStore(appStore);
  }

  @computed
  get isFetchingTaskIds(): boolean {
    return this.taskIdsFetcher.isLoading.value;
  }

  @action.bound
  resetPagination(): void {
    this.filtersStore.pagination.setFromPaginatorInfo({
      perPage: 10,
      currentPage: 1,
      total: undefined,
      lastItem: undefined,
    });
  }

  abstract fetchTasks(): Promise<void>;

  @action.bound
  async fetchTaskIds(): Promise<void> {
    const [data, error] = await this.taskIdsFetcher.fetch({
      filters: {
        search: this.filtersStore.queryParams.filters.search,
        where: {
          AND: this.filtersStore.queryParams.filters.where?.AND.filter(
            (filter) =>
              !("column" in filter) ||
              filter.column !== TaskWhereColumn.OnlyParent,
          ),
        },
      },
    });

    if (error || !data.taskIds) return;
    this.bulkActionsStore.setAllIds(data.taskIds);
  }

  @action.bound
  updateTask(_updatedTask: { id: string } & Partial<TaskBaseFragment>): void {}

  @action.bound
  removeTask(_taskId: string, _parentId?: string): void {}

  /** Assignees or the current assignee of the task changed. */
  @action.bound
  async handleTaskAssigneesChanged(
    _task: TaskBaseFragment,
    updatedTask: { id: string } & Partial<TaskBaseFragment>,
  ): Promise<void> {
    this.updateTask(updatedTask);
  }

  @action.bound
  handleCopyId(taskId: string): void {
    navigator.clipboard.writeText(
      `${location.origin}/notifications?${TASK_ID_QUERY_KEY}=${taskId}`,
    );
    this.appStore.UIStore.toast({
      title: t`Link copied to clipboard`,
      status: "info",
      isClosable: true,
      duration: 3000,
    });
  }
}
